import EditIcon from '@mui/icons-material/EditRounded';
import { getChannelIcon } from '@helpers/channels';
import { toTimestamp } from '@helpers/dates';
import DeleteIcon from '@mui/icons-material/DeleteRounded';
import { Typography } from '@components/Typography';
import ActionIconButton from '@components/ActionIconButton';
import { CHANNEL, CHANNEL_MAP, STATUS, WhatsAppHistoryImportStatus } from '@constants/channels';
import { useTranslation, useHasAccessToAsset, useLocations } from '@hooks';
import { CardDivider } from '@components/CardItem/styles';
import CardOwnerLabel from '@components/CardItem/OwnerLabel';
import OwnerIcon, { OWNER_TYPE } from '@components/OwnerIcon';
import {
    StyledIconButton,
    Container,
    Footer,
    Controls,
    Header,
    TitleWrapper,
    ChannelIconWrapper,
    TeamWrapper,
} from './styles';

const HISTORY_IMPORT_CAPTIONS = {
    [WhatsAppHistoryImportStatus.Running]: 'SettingsChannels.historyImportRunning',
    [WhatsAppHistoryImportStatus.Completed]: 'SettingsChannels.historyImportCompleted',
    [WhatsAppHistoryImportStatus.Declined]: 'SettingsChannels.historyImportDeclined',
    [WhatsAppHistoryImportStatus.Failed]: 'SettingsChannels.historyImportFailed',
};

const HISTORY_IMPORT_WINDOW_MS = 24 * 60 * 60 * 1000;

const getHistoryImportCaption = whatsAppParameters => {
    const status = whatsAppParameters?.historyImportStatus;
    const outlivedTheImportWindow =
        status === WhatsAppHistoryImportStatus.Running &&
        Date.now() - toTimestamp(whatsAppParameters.historyImportStartedAt) > HISTORY_IMPORT_WINDOW_MS;
    return outlivedTheImportWindow ? null : HISTORY_IMPORT_CAPTIONS[status];
};

const ChannelItem = ({ channel, companies, brands, isSubmitting, onClickDelete, onClickEdit }) => {
    const { t } = useTranslation();
    const { hasAccessToChannel } = useHasAccessToAsset();
    const { hasSingleLocation } = useLocations();

    const hasAccessToAsset = hasAccessToChannel();

    const legacyOwnerName = channel.locationId
        ? companies.find(c => c.companyId === channel.locationId)?.internalName
        : brands.find(b => b.id === channel.brandId)?.title;
    const legacyOwnerType = channel.locationId ? OWNER_TYPE.LOCATION : OWNER_TYPE.BRAND;
    const teamName = channel.teamName || t('common.none');
    const historyImportCaption = getHistoryImportCaption(channel.whatsAppParameters);

    return (
        <Container>
            <Header>
                <TitleWrapper>
                    <ChannelIconWrapper title={CHANNEL_MAP[channel?.type]}>
                        {getChannelIcon(channel.type)}
                    </ChannelIconWrapper>
                    <Typography variant="subtitle2" noWrap>
                        {channel.name}
                    </Typography>
                </TitleWrapper>
                {(channel?.type === CHANNEL.WHATSAPP ||
                    channel?.type === CHANNEL.WHATSAPP_BUSINESS ||
                    channel?.type === CHANNEL.EMAIL) &&
                channel?.status === STATUS.PENDING ? (
                    <Typography variant="caption" sx={{ color: '#FF8700' }}>
                        {t('SettingsChannels.pending')}
                    </Typography>
                ) : null}
                {channel?.status === STATUS.DISCONNECTED ? (
                    <Typography variant="caption" sx={{ color: 'var(--error-main)' }}>
                        {t('common.disconnected')}
                    </Typography>
                ) : null}
                {historyImportCaption ? (
                    <Typography variant="caption" sx={{ color: 'var(--text-secondary)' }}>
                        {t(historyImportCaption)}
                    </Typography>
                ) : null}
                {channel?.type === CHANNEL.LIVE_CHAT && channel?.followUpChannelMissing ? (
                    <Typography variant="caption" sx={{ color: 'var(--warning-main)' }}>
                        {t('SettingsChannels.followUpWarning')}
                    </Typography>
                ) : null}
            </Header>
            <CardOwnerLabel type={legacyOwnerType} name={hasSingleLocation ? null : legacyOwnerName} />
            <CardDivider />
            <Footer>
                <TeamWrapper>
                    <Typography variant="caption" color="textSecondary">
                        {t('common.team')}:
                    </Typography>
                    {channel.teamName ? <OwnerIcon type={OWNER_TYPE.TEAM} /> : null}
                    <Typography variant="caption" color="textSecondary">
                        {teamName}
                    </Typography>
                </TeamWrapper>
                {hasAccessToAsset ? (
                    <Controls columns={2}>
                        <ActionIconButton
                            title={t('common.edit')}
                            component={StyledIconButton}
                            disabled={
                                isSubmitting ||
                                (channel.type === CHANNEL.EMAIL && channel.isDefault) ||
                                (channel.type === CHANNEL.SMS && channel.isDefault)
                            }
                            onClick={() => onClickEdit(channel)}
                        >
                            <EditIcon />
                        </ActionIconButton>
                        <ActionIconButton
                            title={t('common.delete')}
                            component={StyledIconButton}
                            disabled={
                                isSubmitting ||
                                (channel.type === CHANNEL.EMAIL && channel.isDefault) ||
                                (channel.type === CHANNEL.SMS && channel.isDefault)
                            }
                            onClick={() => onClickDelete(channel)}
                        >
                            <DeleteIcon />
                        </ActionIconButton>
                    </Controls>
                ) : null}
            </Footer>
        </Container>
    );
};

export default ChannelItem;
